/**
 * 서버 점검 패널 (D-02) — 최고관리자 전용 히든 메뉴
 *
 * 왜 히든인가:
 *   고객사 담당자에게는 필요 없는 화면이고, 서버 내부 상태(디스크·서비스명 등)가
 *   그대로 보이므로 메뉴에 노출하지 않는다.
 *   다만 <숨기는 것은 방어가 아니다> — 실제 접근 통제는 서버에서 한다
 *   (routes/systemHealthRoutes.js 의 SUPER_ADMIN 확인).
 *
 * 여는 방법:
 *   우측 상단 사용자 이름을 5번 연속 클릭 → 비밀번호 입력 → 이 패널
 */
import React, { useCallback, useEffect, useState } from "react";

const PASSCODE = process.env.REACT_APP_HEALTH_PASSCODE || "mintblue";

export default function SystemHealthPanel({ open, onClose }) {
  const [step, setStep] = useState("auth");   // auth | panel
  const [pw, setPw] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [a, b] = await Promise.all([
        fetch("/api/system-health", { credentials: "include" }),
        fetch("/api/system-health/history?limit=30", { credentials: "include" }),
      ]);
      if (a.status === 404 || b.status === 404) {
        setErr("이 계정에는 권한이 없습니다.");
        setData(null);
        return;
      }
      const j = await a.json();
      const h = await b.json();
      setData(j);
      setHistory(h.items || []);
    } catch (e) {
      setErr("조회 실패: " + (e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  // 점검 모드 — 작업 중 알림을 잠시 멈춘다.
  //   영구히 끄는 것이 아니라 지정 시간이 지나면 서버가 자동으로 다시 켠다.
  const setMute = useCallback(
    async (minutes) => {
      try {
        const url = minutes
          ? "/api/system-health/mute"
          : "/api/system-health/unmute";
        await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ minutes }),
        });
        await load();
      } catch (e) {
        setErr("점검 모드 변경 실패: " + (e.message || e));
      }
    },
    [load]
  );

  // 조치 확인 — "봤다, 내가 조치한다".
  //   그 건에 한해 재알림만 멈춘다. 문제가 해소되면 서버가 확인 표시를 자동으로 지운다.
  const ack = useCallback(
    async (body) => {
      try {
        await fetch("/api/system-health/ack", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        await load();
      } catch (e) {
        setErr("확인 처리 실패: " + (e.message || e));
      }
    },
    [load]
  );

  useEffect(() => {
    if (!open) {
      setStep("auth");
      setPw("");
      setPwMsg("");
      setData(null);
      return;
    }
  }, [open]);

  // 패널이 열려 있는 동안 30초마다 자동 갱신
  useEffect(() => {
    if (step !== "panel") return undefined;
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [step, load]);

  if (!open) return null;

  const submitPw = (e) => {
    e.preventDefault();
    if (pw === PASSCODE) {
      setStep("panel");
    } else {
      setPwMsg("비밀번호가 맞지 않습니다.");
      setPw("");
    }
  };

  const ok = data && data.status === "ok";

  return (
    <div className="fixed inset-0 z-[100002] flex items-center justify-center bg-black/60 p-4">
      {/* 비밀번호 단계는 입력칸 하나뿐이라 좁게 — 넓으면 허전해 보인다 */}
      <div
        className={`max-h-[88vh] w-full overflow-auto rounded-2xl bg-white p-5 shadow-2xl dark:bg-gray-900 ${
          step === "auth" ? "max-w-xs" : "max-w-3xl"
        }`}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">서버 점검</h2>
          <button
            onClick={onClose}
            className="rounded-lg px-3 py-1 text-sm font-semibold text-gray-500 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
          >
            닫기
          </button>
        </div>

        {step === "auth" && (
          <form onSubmit={submitPw} className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              관리자 확인이 필요합니다.
            </p>
            <input
              type="password"
              value={pw}
              autoFocus
              onChange={(e) => setPw(e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-white/5 dark:text-gray-100"
              placeholder="비밀번호"
            />
            {pwMsg && <p className="text-sm text-red-600">{pwMsg}</p>}
            <button
              type="submit"
              className="w-full rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              확인
            </button>
          </form>
        )}

        {step === "panel" && (
          <div className="space-y-5">
            {err && (
              <div className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
                {err}
              </div>
            )}

            {data && (
              <>
                <div
                  className={`rounded-xl p-4 ${
                    ok
                      ? "bg-green-50 dark:bg-green-500/10"
                      : "bg-red-50 dark:bg-red-500/10"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-base font-bold ${
                        ok ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"
                      }`}
                    >
                      {ok ? "🟢 정상" : "🔴 이상 발생"}
                    </span>
                    <button
                      onClick={load}
                      disabled={loading}
                      className="rounded-lg bg-white/70 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white dark:bg-white/10 dark:text-gray-200"
                    >
                      {loading ? "확인 중..." : "새로고침"}
                    </button>
                  </div>
                  {/* 이상 건 목록 — 건마다 「확인」.
                      확인하면 그 건은 재알림이 멈추고, 해소되면 표시가 자동으로 풀린다. */}
                  {!ok && Array.isArray(data.issues) && data.issues.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {data.issues.map((it) => (
                        <div
                          key={it.key}
                          className="flex items-start justify-between gap-2 rounded-lg bg-white/60 p-2 dark:bg-white/5"
                        >
                          <div className="min-w-0">
                            <p
                              className={`text-sm font-semibold ${
                                it.acked
                                  ? "text-gray-500 line-through dark:text-gray-400"
                                  : "text-red-700 dark:text-red-300"
                              }`}
                            >
                              {it.text}
                            </p>
                            {it.detail && (
                              <p className="text-xs text-gray-600 dark:text-gray-400">{it.detail}</p>
                            )}
                          </div>
                          <button
                            onClick={() => ack({ key: it.key, acked: !it.acked })}
                            className={`shrink-0 rounded-lg px-3 py-1 text-xs font-semibold ${
                              it.acked
                                ? "bg-gray-200 text-gray-600 hover:bg-gray-300 dark:bg-white/10 dark:text-gray-300"
                                : "bg-gray-800 text-white hover:bg-gray-700"
                            }`}
                            title={
                              it.acked
                                ? "확인을 취소하면 다시 알림이 옵니다"
                                : "확인하면 이 건은 더 이상 알림이 오지 않습니다"
                            }
                          >
                            {it.acked ? "확인 취소" : "확인"}
                          </button>
                        </div>
                      ))}
                      {data.issues.some((i) => !i.acked) && (
                        <div className="flex justify-end">
                          <button
                            onClick={() => ack({ all: true, acked: true })}
                            className="rounded-lg px-2 py-1 text-xs font-semibold text-gray-600 underline hover:text-gray-800 dark:text-gray-300"
                          >
                            전체 확인
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        확인한 건은 재알림이 멈춥니다. 문제가 해소되면 자동으로 풀리고,
                        다시 생기면 그때 새로 알립니다.
                      </p>
                    </div>
                  )}
                  {/* issues 를 못 받는 구버전 대비 */}
                  {!ok && !(Array.isArray(data.issues) && data.issues.length > 0) && (
                    <ul className="mt-2 list-inside list-disc text-sm text-red-700 dark:text-red-300">
                      {data.problems.map((p, i) => (
                        <li key={i} className="font-semibold">{p}</li>
                      ))}
                    </ul>
                  )}
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    점검 시각 {new Date(data.checkedAt).toLocaleString("ko-KR")}
                  </p>

                  {/* 점검 모드 — 재시작·설치 작업 중 알림이 쏟아지는 것을 막는다 */}
                  <div className="mt-3 border-t border-black/5 pt-3 dark:border-white/10">
                    {data.mute && data.mute.muted ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-lg bg-amber-100 px-2 py-1 text-xs font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">
                          🔕 알림 꺼짐 · {Math.ceil(data.mute.remainSec / 60)}분 남음
                        </span>
                        <button
                          onClick={() => setMute(0)}
                          className="rounded-lg bg-gray-800 px-3 py-1 text-xs font-semibold text-white hover:bg-gray-700"
                        >
                          지금 다시 켜기
                        </button>
                      </div>
                    ) : (
                      // 안내 문구와 버튼을 같은 줄에 두면 30분 버튼만 위로 붙어
                      // 줄이 어긋난다 → 문구는 윗줄, 버튼 3개는 아랫줄에 나란히
                      <div>
                        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                          작업 전에 눌러두면 그동안 알림이 오지 않습니다 (시간이 지나면 자동 복구)
                        </p>
                        <div className="flex items-center gap-2">
                          {[30, 60, 120].map((m) => (
                            <button
                              key={m}
                              onClick={() => setMute(m)}
                              className="flex-1 whitespace-nowrap rounded-lg bg-white/80 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-white dark:bg-white/10 dark:text-gray-200"
                            >
                              🔕 {m}분간 끄기
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 항목별 점검 결과 — 이상이 없을 때도 <무엇을 어디까지 보고 있는지>
                    보이지 않으면 "정말 감시가 되고 있나"를 확인할 방법이 없다. */}
                {Array.isArray(data.checks) && data.checks.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                      점검 항목
                    </h3>
                    {["기반", "서비스"].map((g) => {
                      const rows = data.checks.filter((c) => c.group === g);
                      if (!rows.length) return null;
                      return (
                        <div key={g} className="mb-3">
                          <p className="mb-1 text-xs font-semibold text-gray-500 dark:text-gray-400">
                            {g === "기반" ? "기반 상태" : "서비스"}
                          </p>
                          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
                            <table className="w-full text-xs">
                              <tbody className="text-gray-700 dark:text-gray-300">
                                {rows.map((c, i) => (
                                  <tr
                                    key={c.key || i}
                                    className="border-b border-gray-100 last:border-0 dark:border-white/5"
                                  >
                                    <td className="whitespace-nowrap p-2 font-medium">
                                      <span className={c.ok ? "text-green-600" : "text-red-600"}>
                                        ●
                                      </span>{" "}
                                      {c.label}
                                    </td>
                                    <td
                                      className={`whitespace-nowrap p-2 text-right ${
                                        c.ok ? "" : "font-bold text-red-600"
                                      }`}
                                    >
                                      {c.state}
                                    </td>
                                    <td className="whitespace-nowrap p-2 text-right text-gray-400">
                                      {c.note}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {Array.isArray(data.heartbeat) && data.heartbeat.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                      AI 추론 상태
                    </h3>
                    {/* 헤더가 줄바꿈되면 표가 2줄로 보여 읽기 나쁘다 → 한 줄 고정 */}
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300">
                          <tr>
                            <th className="whitespace-nowrap p-2 text-left">서비스</th>
                            <th className="whitespace-nowrap p-2 text-right">스트림</th>
                            <th className="whitespace-nowrap p-2 text-right">감지 대상</th>
                            <th className="whitespace-nowrap p-2 text-left">설정</th>
                            <th className="whitespace-nowrap p-2 text-right">마지막 보고</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700 dark:text-gray-300">
                          {data.heartbeat.map((h) => (
                            <tr key={h.ServiceName} className="border-t border-gray-200 dark:border-white/10">
                              <td className="whitespace-nowrap p-2">{h.ServiceName}</td>
                              <td className="whitespace-nowrap p-2 text-right">{h.StreamCount}</td>
                              <td className={`whitespace-nowrap p-2 text-right ${h.StreamCount > 0 && h.TargetCount === 0 ? "font-bold text-red-600" : ""}`}>
                                {h.TargetCount}
                              </td>
                              <td className="whitespace-nowrap p-2">{h.StreamPrefix || "-"}</td>
                              <td className="whitespace-nowrap p-2 text-right">{h.AgeSec}초 전</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            <div>
              <h3 className="mb-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                최근 이상·복구 이력
              </h3>
              {history.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">기록이 없습니다.</p>
              ) : (
                <div className="max-h-64 overflow-auto rounded-xl border border-gray-200 dark:border-white/10">
                  {history.map((h) => (
                    <div key={h.idx} className="border-b border-gray-100 p-2 text-xs last:border-0 dark:border-white/5">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{h.title}</span>
                        <span className="text-gray-400">{h.createdAt}</span>
                      </div>
                      <div className="mt-0.5 whitespace-pre-wrap text-gray-600 dark:text-gray-400">{h.body}</div>
                      <div className="mt-0.5 text-gray-400">
                        수신 {h.recipients}대 · {h.status}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
